"use strict";
export class ConditionalFormHandling {
  constructor(conditionalFieldMap, formId) {
    this.conditionalFieldMapping = conditionalFieldMap;
    this.formElement = document.querySelector(`hi-form#${formId}`);
    if (this.formElement) {
      this.initializeFormWithConditionalLogic();
    }
  }
  initializeFormWithConditionalLogic() {
    if (!this.formElement) return;
    Object.keys(this.conditionalFieldMapping).forEach((nodeId) => {
      const controllingElement = this.formElement.querySelector(`[data-node-id="${nodeId}"]`);
      if (!controllingElement) return;
      let initialValue;
      switch (controllingElement.tagName) {
        case "HI-COMBOBOX":
          initialValue = controllingElement.multiple ? controllingElement.values : controllingElement.value;
          break;
        default:
          initialValue = controllingElement.value;
      }
      if (initialValue !== void 0) {
        this.lookForMatchingLogic(nodeId, initialValue);
      }
    });
    this.syncDocumentsSectionVisibility();
  }
  addFormEventListener() {
    if (!this.formElement) return;
    this._valueChangedHandler = (event) => {
      if (!event.detail.originElement) return;
      const controllingFieldValue = event.detail.srcValue;
      const controllingFieldNodeId = event.detail.originElement.dataset.nodeId;
      this.lookForMatchingLogic(controllingFieldNodeId, controllingFieldValue);
    };
    this.formElement.addEventListener("value-changed", this._valueChangedHandler);
  }
  removeFormEventListener() {
    if (this._valueChangedHandler && this.formElement) {
      this.formElement.removeEventListener("value-changed", this._valueChangedHandler);
      this._valueChangedHandler = null;
    }
  }
  lookForMatchingLogic(controllingFieldNodeId, controllingFieldValue) {
    const matchingLogic = this.conditionalFieldMapping[controllingFieldNodeId] || [];
    if (!matchingLogic.length) return;
    this.handleMatchingLogic(matchingLogic, controllingFieldValue);
  }
  handleMatchingLogic(matchingLogic, triggeredValue) {
    let documentVisibilityChanged = false;
    matchingLogic.forEach((logic) => {
      const targetContainer = this.getTargetContainer(logic);
      const valuesMatch = this.compareValues(logic.trigger_value, triggeredValue, logic.trigger_type);
      const shouldHide = this.shouldHideTarget(logic, valuesMatch);
      targetContainer.classList.toggle("hide", shouldHide);
      if (this.isSectionNode(logic)) {
        this.applySectionSideEffects(targetContainer, !shouldHide);
      } else if (this.isDocumentNode(targetContainer)) {
        this.applyDocumentSideEffects(targetContainer, !shouldHide, logic);
        documentVisibilityChanged = true;
      } else {
        this.applyElementSideEffects(targetContainer, logic, valuesMatch);
      }
    });
    if (documentVisibilityChanged) this.syncDocumentsSectionVisibility();
  }
  isDocumentNode(targetContainer) {
    return this.getFormControl(targetContainer)?.dataset.nodeType === "document";
  }
  applyDocumentSideEffects(targetContainer, documentIsVisible, logic) {
    if (!targetContainer) return;
    const control = this.getFormControl(targetContainer);
    if (!control) return;
    if (control.dataset.originalName === void 0) {
      control.dataset.originalName = control.getAttribute("name") || "";
    }
    if (documentIsVisible) {
      if (!control.getAttribute("name") && control.dataset.originalName) {
        control.setAttribute("name", control.dataset.originalName);
      }
    } else if (control.hasAttribute("name")) {
      control.removeAttribute("name");
    }
    this.toggleRequiredForElement(targetContainer, documentIsVisible && logic["field_required"] !== false);
  }
  syncDocumentsSectionVisibility() {
    const section = this.formElement?.querySelector('[data-documents-section="true"]');
    if (!section) return;
    const documentGroups = section.querySelectorAll("hi-input-group");
    const anyVisible = Array.from(documentGroups).some((group) => !group.classList.contains("hide"));
    section.classList.toggle("hide", !anyVisible);
  }
  applySectionSideEffects(sectionGroup, sectionIsVisible) {
    this.setSerializeForSectionChildren(sectionGroup, sectionIsVisible);
    this.toggleRequiredForSectionChildren(sectionGroup, sectionIsVisible);
  }
  applyElementSideEffects(targetContainer, logic, valuesMatch) {
    if (this.shouldHideElements(logic, valuesMatch)) {
      this.updateValues(targetContainer, logic);
      this.removeRequired(targetContainer);
    } else if (this.shouldShowElements(logic)) {
      this.addRequired(targetContainer, logic);
    }
  }
  // Resolve the DOM container for the target
  // - sections : the section itself
  // - elements : the surrounding hi-input-group (so label + control are updated together)
  getTargetContainer(logic) {
    const elementToControl = this.formElement?.querySelector(`[data-node-id="${logic.node_id}"]`) || document.querySelector(`[data-node-id="${logic.node_id}"]`);
    if (!elementToControl) return null;
    if (this.isSectionNode(logic)) {
      return elementToControl;
    } else {
      return elementToControl.closest("hi-input-group");
    }
  }
  isSectionNode(logic) {
    return logic.node_id.includes("section");
  }
  shouldHideTarget(logic, valuesMatch) {
    if (logic.trigger_action === "hide" && valuesMatch) return true;
    if (logic.trigger_action === "show" && !valuesMatch) return true;
    return false;
  }
  toggleRequiredForSectionChildren(sectionElement, sectionIsVisible) {
    if (!sectionElement) return;
    const groups = sectionElement.querySelectorAll("hi-input-group");
    groups.forEach((group) => {
      const label = group.querySelector("hi-label");
      const control = this.getFormControl(group);
      const originalRequired = control ? control.dataset.originalRequired === "true" : false;
      const required = sectionIsVisible ? originalRequired : false;
      if (label) {
        if (required) {
          label.setAttribute("required", "");
        } else {
          label.removeAttribute("required");
        }
      }
      if (control) {
        control.required = required;
      }
    });
  }
  setSerializeForSectionChildren(sectionElement, sectionIsVisible) {
    if (!sectionElement) return;
    const controls = sectionElement.querySelectorAll("hi-input, hi-textarea, hi-select, hi-combobox, hi-checkbox");
    controls.forEach((control) => {
      if (control.dataset.originalName === void 0) {
        control.dataset.originalName = control.getAttribute("name") || "";
      }
      if (sectionIsVisible) {
        if (!control.getAttribute("name") && control.dataset.originalName) {
          control.setAttribute("name", control.dataset.originalName);
        }
      } else {
        if (control.hasAttribute("name")) {
          control.removeAttribute("name");
        }
      }
    });
  }
  shouldHideElements(logic, valuesMatch) {
    if (logic.node_id.includes("section")) return false;
    if (logic.trigger_action === "hide" && valuesMatch) return true;
    if (!valuesMatch) return true;
    return false;
  }
  shouldShowElements(logic) {
    if (logic.node_id.includes("section")) return false;
    return true;
  }
  updateValues(groupToControl, matchingLogic) {
    if (matchingLogic.default_value === null) {
      this.clearValues(groupToControl);
    } else {
      this.updateDefaultValue(groupToControl, matchingLogic);
    }
  }
  clearValues(elementToControl) {
    const elementToChange = this.getFormControl(elementToControl);
    switch (elementToChange.tagName) {
      case "HI-COMBOBOX": {
        elementToChange.clearInput();
        break;
      }
      default: {
        if (typeof elementToChange.clear === "function") elementToChange.clear();
      }
    }
  }
  isDefaultValueEmpty(defaultValue) {
    return defaultValue === null || defaultValue === void 0 || defaultValue === "" || Array.isArray(defaultValue) && defaultValue.length === 0;
  }
  updateDefaultValue(elementToControl, matchingLogic) {
    const defaultValue = matchingLogic.default_value;
    if (this.isDefaultValueEmpty(defaultValue)) return;
    const elementToChange = this.getFormControl(elementToControl);
    switch (elementToChange.tagName) {
      case "HI-COMBOBOX": {
        elementToChange.values = [defaultValue];
        break;
      }
      case "HI-INPUT": {
        if (elementToChange.type === "date") {
          const date = new Date(defaultValue[0]);
          elementToChange.value = date.toLocaleDateString("en-CA");
        } else {
          elementToChange.value = defaultValue[0];
        }
        break;
      }
      default: {
        elementToChange.value = defaultValue[0];
      }
    }
  }
  getFormControl(elementToControl) {
    return elementToControl.querySelector("[data-node-id]");
  }
  removeRequired(elementToControl) {
    this.toggleRequiredForElement(elementToControl, false);
  }
  addRequired(elementToControl, matchingLogic) {
    if (matchingLogic["field_required"] === false) return;
    this.toggleRequiredForElement(elementToControl, true);
  }
  toggleRequiredForElement(elementToControl, required) {
    const label = elementToControl.querySelector("hi-label");
    if (label) {
      if (required) {
        label.setAttribute("required", "");
      } else {
        label.removeAttribute("required");
      }
    }
    const control = this.getFormControl(elementToControl);
    if (control) control.required = required;
  }
  /**
   * getValueToLookfor(valueToLookFor, triggerType)
   *
   * This function processes the `valueToLookFor` parameter based on the `triggerType` and returns a flat array of values
   * that will trigger the condition. It ensures the values are formatted consistently for comparison.
   *
   * @param {string | Array} valueToLookFor - The value(s) to look for, which can be a string or an array.
   * @param {string} triggerType - The type of trigger that determines how the value should be processed.
   *
   * @returns {Array} A flat array of processed values that will trigger the condition.
   */
  getValueToLookfor(valueToLookFor, triggerType) {
    let formattedReturnValue = valueToLookFor;
    switch (triggerType) {
      case "any":
      case "none": {
        formattedReturnValue = valueToLookFor[0].split(",").map((item) => item.trim());
        break;
      }
      default: {
        break;
      }
    }
    return this.flattenArrayAndLowerCaseValues(formattedReturnValue);
  }
  flattenArrayAndLowerCaseValues(valueToLookFor) {
    return valueToLookFor.flat().map((item) => item.toString().toLowerCase());
  }
  getTriggeredValue(triggeredValue) {
    if (this.isTriggeredValueEmpty(triggeredValue)) return triggeredValue;
    return Array.isArray(triggeredValue) ? triggeredValue.map((item) => item.toString().toLowerCase()) : triggeredValue.toString().toLowerCase();
  }
  compareValues(valueToLookFor, triggeredValue, triggerType) {
    valueToLookFor = this.getValueToLookfor(valueToLookFor, triggerType);
    triggeredValue = this.getTriggeredValue(triggeredValue);
    const currentDate = /* @__PURE__ */ new Date((/* @__PURE__ */ new Date()).toISOString().split("T")[0] + "T00:00:00");
    switch (triggerType) {
      case "after":
        return new Date(triggeredValue) > new Date(valueToLookFor[0]);
      case "any":
        return valueToLookFor.some((val) => triggeredValue.includes(val));
      case "before":
        return new Date(triggeredValue) < new Date(valueToLookFor[0]);
      case "between":
        return this.between(valueToLookFor, triggeredValue);
      case "contains":
        return valueToLookFor.some((val) => triggeredValue.includes(val));
      case "contains_all":
        return valueToLookFor.every((value) => triggeredValue.includes(value));
      case "contains_any":
        return this.containsAny(valueToLookFor, triggeredValue);
      case "current":
        return this.currentTimeframe(currentDate, valueToLookFor[0], /* @__PURE__ */ new Date(triggeredValue + "T00:00:00"));
      case "custom":
        return new Date(triggeredValue) > new Date(valueToLookFor[0]) && new Date(triggeredValue) < new Date(valueToLookFor[1]);
      case "does_not_contain_all":
        return !valueToLookFor.every((val) => triggeredValue.includes(val));
      case "does_not_contain_any":
        return this.doesNotContainAny(valueToLookFor, triggeredValue);
      case "does_not_equal":
        return valueToLookFor[0] !== triggeredValue;
      case "ends_with":
        return triggeredValue.endsWith(valueToLookFor[0]) && triggeredValue !== "";
      case "exactly":
        return valueToLookFor[0] === triggeredValue;
      case "from":
        return this.from(valueToLookFor, triggeredValue);
      case "greater_than":
        return triggeredValue > valueToLookFor[0];
      case "greater_than_or_equal_to":
        return triggeredValue >= valueToLookFor[0];
      case "is":
        return valueToLookFor[0] === triggeredValue;
      case "is_empty":
        return triggeredValue === "" || triggeredValue === void 0 || triggeredValue?.length === 0;
      case "is_exactly":
        return valueToLookFor[0] === triggeredValue;
      case "is_not":
        return valueToLookFor[0] !== triggeredValue;
      case "is_not_empty":
        return this.isNotEmpty(triggeredValue);
      case "less_than":
        return triggeredValue < valueToLookFor[0];
      case "less_than_or_equal_to":
        return triggeredValue <= valueToLookFor[0];
      case "last":
        return this.dateInRange("last", valueToLookFor[0], triggeredValue, currentDate);
      case "next":
        return this.dateInRange("next", valueToLookFor[0], triggeredValue, currentDate);
      case "none":
        return !valueToLookFor.some((val) => triggeredValue.includes(val));
      case "since":
        return new Date(triggeredValue) > new Date(valueToLookFor[0]);
      case "starts_with":
        return triggeredValue.startsWith(valueToLookFor[0]) && triggeredValue !== "";
      case "to":
        return this.to(valueToLookFor, triggeredValue);
      default:
        throw new Error("Unsupported operator");
    }
  }
  isNotEmpty(triggeredValue) {
    if (Array.isArray(triggeredValue)) {
      return triggeredValue.length > 0;
    }
    return triggeredValue !== "" && triggeredValue !== void 0;
  }
  extractNumericPart(value) {
    const match = value.match(/^\d+/);
    return match ? parseInt(match[0], 10) : null;
  }
  startsWithNumber(value) {
    return /^\d/.test(value);
  }
  between(valueToLookFor, triggeredValue) {
    if (this.isTriggeredValueEmpty(triggeredValue)) return false;
    const startNum = parseFloat(valueToLookFor[0]);
    const endNum = parseFloat(valueToLookFor[1]);
    const triggeredNum = parseFloat(triggeredValue);
    if (!isNaN(startNum) && !isNaN(endNum) && !isNaN(triggeredNum)) {
      return triggeredNum >= startNum && triggeredNum <= endNum;
    }
    if (this.startsWithNumber(valueToLookFor[0]) && this.startsWithNumber(valueToLookFor[1])) {
      if (this.startsWithNumber(triggeredValue)) {
        const numericTriggeredValue = this.extractNumericPart(triggeredValue);
        const numericValueToLookForStart = this.extractNumericPart(valueToLookFor[0]);
        const numericValueToLookForEnd = this.extractNumericPart(valueToLookFor[1]);
        if (numericTriggeredValue !== null && numericValueToLookForStart !== null && numericValueToLookForEnd !== null) {
          if (numericTriggeredValue > numericValueToLookForStart && numericTriggeredValue < numericValueToLookForEnd) return true;
          if (numericTriggeredValue === numericValueToLookForStart) return triggeredValue.localeCompare(valueToLookFor[0]) >= 0;
          if (numericTriggeredValue === numericValueToLookForEnd) return triggeredValue.localeCompare(valueToLookFor[1]) <= 0;
          return false;
        } else {
          return false;
        }
      }
    }
    return triggeredValue.localeCompare(valueToLookFor[0]) >= 0 && triggeredValue.localeCompare(valueToLookFor[1]) <= 0;
  }
  to(valueToLookFor, triggeredValue) {
    if (this.isTriggeredValueEmpty(triggeredValue)) return true;
    if (this.startsWithNumber(valueToLookFor[0])) {
      if (this.startsWithNumber(triggeredValue)) {
        const numericTriggeredValue = this.extractNumericPart(triggeredValue);
        const numericValueToLookTo = this.extractNumericPart(valueToLookFor[0]);
        if (numericTriggeredValue !== null && numericValueToLookTo !== null) {
          if (numericTriggeredValue < numericValueToLookTo) return true;
          if (numericTriggeredValue === numericValueToLookTo) return triggeredValue.localeCompare(valueToLookFor[0]) <= 0;
          return false;
        } else {
          return false;
        }
      }
    }
    return triggeredValue.localeCompare(valueToLookFor[0]) <= 0;
  }
  from(valueToLookFor, triggeredValue) {
    if (this.isTriggeredValueEmpty(triggeredValue)) return false;
    if (this.startsWithNumber(valueToLookFor[0])) {
      if (this.startsWithNumber(triggeredValue)) {
        const numericTriggeredValue = this.extractNumericPart(triggeredValue);
        const numericValueToLookFrom = this.extractNumericPart(valueToLookFor[0]);
        if (numericTriggeredValue !== null && numericValueToLookFrom !== null) {
          if (numericTriggeredValue > numericValueToLookFrom) return true;
          if (numericTriggeredValue === numericValueToLookFrom) return triggeredValue.localeCompare(valueToLookFor[0]) >= 0;
          return false;
        } else {
          return false;
        }
      }
    }
    return valueToLookFor[0].localeCompare(triggeredValue) <= 0;
  }
  containsAny(valueToLookFor, triggeredValue) {
    if (Array.isArray(triggeredValue)) {
      if (!triggeredValue.length) return false;
      return triggeredValue.some((val) => valueToLookFor.includes(val));
    } else {
      if (this.isTriggeredValueEmpty(triggeredValue)) return false;
      return valueToLookFor.includes(triggeredValue);
    }
  }
  doesNotContainAny(valueToLookFor, triggeredValue) {
    if (!valueToLookFor.length) return true;
    if (this.isTriggeredValueEmpty(triggeredValue)) return true;
    return !valueToLookFor.some((val) => triggeredValue.includes(val));
  }
  isTriggeredValueEmpty(triggeredValue) {
    return [void 0, null, ""].includes(triggeredValue);
  }
  dateInRange(direction, rangeType, dateToCheck, currentDate) {
    const [year, month, day] = dateToCheck.split("-").map(Number);
    dateToCheck = new Date(Date.UTC(year, month - 1, day, 6, 0, 0));
    let rangeStart = new Date(currentDate);
    let rangeEnd = new Date(currentDate);
    let [, number, unit] = rangeType.match(/(\d+)_((?:\w+_?)+)/);
    number = parseInt(number);
    switch (unit) {
      case "day":
      case "days":
        if (direction === "last") {
          rangeStart.setDate(currentDate.getDate() - number);
          rangeEnd.setDate(currentDate.getDate());
        } else {
          rangeStart.setDate(currentDate.getDate() + 1);
          rangeEnd.setDate(currentDate.getDate() + 1 + number);
        }
        break;
      case "calendar_week":
      case "calendar_weeks":
        if (direction === "last") {
          rangeStart.setDate(currentDate.getDate() - currentDate.getDay() - 7 * number);
          rangeEnd.setDate(currentDate.getDate() - currentDate.getDay());
        } else {
          rangeStart.setDate(currentDate.getDate() - currentDate.getDay() + 1);
          rangeEnd.setDate(currentDate.getDate() - currentDate.getDay() + 7 * number);
        }
        break;
      case "calendar_month":
      case "calendar_months":
        if (direction === "last") {
          rangeStart = new Date(currentDate.getFullYear(), currentDate.getMonth() - number, 1);
          rangeEnd.setMonth(currentDate.getMonth(), 0);
        } else {
          rangeStart = new Date(currentDate.getFullYear(), currentDate.getMonth() + 1, 1);
          rangeEnd = new Date(currentDate.getFullYear(), currentDate.getMonth() + number + 1, 0);
        }
        break;
      case "year":
        if (direction === "last") {
          rangeStart.setFullYear(currentDate.getFullYear() - number, 0, 1);
          rangeEnd.setFullYear(currentDate.getFullYear() - 1, 11, 31);
        } else {
          rangeStart.setFullYear(currentDate.getFullYear() + 1, 0, 1);
          rangeEnd.setFullYear(currentDate.getFullYear() + number, 11, 31);
        }
        break;
      default:
        throw new Error("Unsupported operator");
    }
    return dateToCheck >= rangeStart && dateToCheck <= rangeEnd;
  }
  currentTimeframe(currentDate, timeframe, date) {
    switch (timeframe) {
      case "current_day": {
        const startOfDay = new Date(currentDate.getFullYear(), currentDate.getMonth(), currentDate.getDate());
        const endOfDay = new Date(currentDate.getFullYear(), currentDate.getMonth(), currentDate.getDate() + 1);
        return date >= startOfDay && date < endOfDay;
      }
      case "current_week": {
        const startOfWeek = new Date(currentDate);
        startOfWeek.setDate(currentDate.getDate() - currentDate.getDay());
        const endOfWeek = new Date(startOfWeek);
        endOfWeek.setDate(startOfWeek.getDate() + 6);
        return date >= startOfWeek && date <= endOfWeek;
      }
      case "current_month": {
        const startOfMonth = new Date(currentDate.getFullYear(), currentDate.getMonth(), 1);
        const endOfMonth = new Date(currentDate.getFullYear(), currentDate.getMonth() + 1, 0);
        return date >= startOfMonth && date <= endOfMonth;
      }
      case "current_year": {
        const startOfYear = new Date(currentDate.getFullYear(), 0, 1);
        const endOfYear = new Date(currentDate.getFullYear() + 1, 0, 1);
        return date >= startOfYear && date < endOfYear;
      }
      default:
        return false;
    }
  }
}
